/**
 * Facturación de tiendas — mapas y estado, en un solo sitio.
 *
 * Antes esto estaba escrito tres veces: en `ShopCard.jsx`, en la lista móvil de
 * `ShopTable.jsx` y otra vez en su tabla de escritorio. Cada copia tenía sus
 * propias etiquetas y su propio `<select>` de plan, así que cambiar un texto
 * obligaba a tocar tres ficheros y era fácil que uno se quedara atrás.
 */
import { PLANES, PLANES_VIGENTES } from './planes';

/**
 * Etiquetas de TODOS los planes, viejos incluidos: un club en `estandar` tiene que
 * ver el nombre de su plan aunque ya no se venda (RF-001).
 */
export const PLAN_LABELS = PLANES.reduce(
  (acc, p) => ({ ...acc, [p.id]: p.name }),
  {}
);

/**
 * Lo que el superadmin puede ASIGNAR: solo los vigentes.
 *
 * Los viejos se pintan pero no se ponen — mover a mano un club a `basico` sería
 * bajarle el tope a 250 socios, que es justo el bloqueo que este cambio evita.
 */
export const SELECTABLE_PLANS = PLANES_VIGENTES.map((p) => ({
  value: p.id,
  label: p.name,
}));

export const VENUE_TYPE_LABELS = {
  cannabis_club: 'Cannabis Club',
  cbd_shop: 'CBD Shop',
  social_club: 'Social Club',
  bistro: 'Bistro',
};

/** Los siete valores que devuelve Stripe. */
export const SUBSCRIPTION_STATUS_LABELS = {
  active: 'Activa',
  trialing: 'Prueba',
  past_due: 'Pago pendiente',
  canceled: 'Cancelada',
  unpaid: 'Impagada',
  incomplete: 'Incompleta',
  incomplete_expired: 'Expirada',
};

/**
 * Un solo estado de cara al usuario.
 *
 * El backend cuenta dos cosas que en realidad son el mismo paso: `activation_status`
 * («Alta») y `subscription_status` («Suscripción»). La tarjeta llegaba a pintar
 * «Alta: Pendiente» y «Suscripción: Sin activar» a la vez para decir lo mismo.
 * Aquí se colapsan en cinco estados, que son los que el usuario distingue.
 */
export const BILLING_STATE = {
  ACTIVE: 'active',
  TRIAL: 'trial',
  PAST_DUE: 'past_due',
  NEEDS_SUBSCRIPTION: 'needs_subscription',
  NOT_STARTED: 'not_started',
  CANCELED: 'canceled',
};

const STATE_META = {
  [BILLING_STATE.ACTIVE]: { label: 'Activa', tone: 'success' },
  [BILLING_STATE.TRIAL]: { label: 'Prueba', tone: 'info' },
  [BILLING_STATE.PAST_DUE]: { label: 'Pago pendiente', tone: 'warning' },
  [BILLING_STATE.NEEDS_SUBSCRIPTION]: { label: 'Falta suscripción', tone: 'warning' },
  [BILLING_STATE.NOT_STARTED]: { label: 'Sin activar', tone: 'neutral' },
  [BILLING_STATE.CANCELED]: { label: 'Cancelada', tone: 'neutral' },
};

/**
 * @param {object} shop  tal cual viene de `/v1/venues`
 * @returns {{
 *   state: string, label: string, tone: string,
 *   planTier: string, planLabel: string,
 *   hasSetupCompleted: boolean, hasSubscription: boolean,
 *   nextStep: string|null, primaryAction: {kind: string, label: string}
 * }}
 */
export function getShopBilling(shop = {}) {
  const planTier = shop.plan_tier || 'basico';
  const planLabel = PLAN_LABELS[planTier] || planTier;
  const subscriptionStatus = shop.subscription_status || null;

  const hasSetupCompleted = shop.activation_status === 'active';
  const hasSubscription = Boolean(subscriptionStatus) && subscriptionStatus !== 'canceled';

  let state;
  if (!hasSetupCompleted) state = BILLING_STATE.NOT_STARTED;
  else if (subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') state = BILLING_STATE.PAST_DUE;
  else if (subscriptionStatus === 'trialing') state = BILLING_STATE.TRIAL;
  else if (subscriptionStatus === 'active') state = BILLING_STATE.ACTIVE;
  else if (subscriptionStatus === 'canceled') state = BILLING_STATE.CANCELED;
  else state = BILLING_STATE.NEEDS_SUBSCRIPTION;

  const meta = STATE_META[state];

  const nextStep = {
    [BILLING_STATE.NOT_STARTED]:
      'Paso 1 de 2. Al pagar el alta en Stripe se te pedirán los datos de pago.',
    [BILLING_STATE.NEEDS_SUBSCRIPTION]:
      'Paso 2 de 2. El alta ya está pagada; queda activar la cuota mensual.',
    [BILLING_STATE.PAST_DUE]:
      'El último cobro ha fallado. Actualiza el método de pago para no perder el servicio.',
    [BILLING_STATE.TRIAL]:
      'Periodo de prueba. La primera cuota se cobrará al terminar.',
    [BILLING_STATE.CANCELED]:
      'La suscripción está cancelada. Puedes volver a activarla cuando quieras.',
    [BILLING_STATE.ACTIVE]: null,
  }[state];

  const primaryAction = {
    [BILLING_STATE.NOT_STARTED]: { kind: 'setup', label: 'Pagar alta en Stripe' },
    [BILLING_STATE.NEEDS_SUBSCRIPTION]: { kind: 'subscribe', label: 'Activar suscripción' },
    [BILLING_STATE.CANCELED]: { kind: 'subscribe', label: 'Reactivar suscripción' },
    [BILLING_STATE.PAST_DUE]: { kind: 'portal', label: 'Actualizar método de pago' },
    [BILLING_STATE.TRIAL]: { kind: 'portal', label: 'Gestionar facturación' },
    [BILLING_STATE.ACTIVE]: { kind: 'portal', label: 'Gestionar facturación' },
  }[state];

  return {
    state,
    label: meta.label,
    tone: meta.tone,
    planTier,
    planLabel,
    subscriptionStatus,
    subscriptionLabel: SUBSCRIPTION_STATUS_LABELS[subscriptionStatus] || meta.label,
    hasSetupCompleted,
    hasSubscription,
    /** Sólo se elige plan mientras no haya suscripción en marcha. */
    canPickPlan: !hasSubscription,
    nextStep,
    primaryAction,
  };
}

/** Clases de la insignia por tono, alineadas con `Club_Badge-status` de Figma. */
export const TONE_CLASSES = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  info: 'bg-sky-50 text-sky-700 border-sky-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  neutral: 'bg-gray-100 text-gray-600 border-gray-200',
};
